import Aos from "aos";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import Column from "../components/Column/Column";
import { NavBar } from "../components/NavBar";
import Row from "../components/Row/Row";
import Text from "../components/Text/Text";

import ImageButton from "../components/Buttons/ImageButton";
import Footer from "../components/Footer/Footer";
import eliska from "../homepage/imgs/eliska.png";
import jazzy from "../homepage/imgs/jazzy.png";
import stan from "../homepage/imgs/stan.png";
import vic from "../homepage/imgs/vic.png";
import email_icon from "./imgs/email_icon.svg";
import linkedin_icon from "./imgs/linkedin_logo.png";

interface AboutUsProps {
  theme: string;
  toggleTheme: any;
}

const AboutUsSection = styled.div`
  background: #000d30;

  @media screen and (max-width: 650px) {
    padding-top: 15%;
  }
`;

const TeamSection = styled.div`
  background: #000000;
  display: flex;
  flex-direction: column;
  padding: 10%;
`;

const AboutUsInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10%;
`;

const OurMissionSection = styled.div`
  background: #000d30;
  background-size: cover;
`;

const OurMissionInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 10%;
  padding-bottom: 10%;
`;

const MissionItems = styled.div`
  display: flex;
  position: relative;
  flex-wrap: wrap;
`;

const MissionItem = styled.div`
  margin-left: 15px;
  flex: 40%;
  padding-bottom: 5%;
  margin-left: auto;
  margin-right: auto;
  &:before {
    content: "";
    display: block;
    width: 8px;
    height: 160px;
    background: #9d7cff;
    margin-left: -20px;
    position: absolute;
  }

  @media screen and (max-width: 1200px) {
    flex: 1 1 auto;
    padding-bottom: 10%;

    &:before {
      content: "";
      display: block;
      width: 8px;
      max-height: 100px;
      min-height: 100px;
      background: #9d7cff;
      margin-left: -20px;
      margin-top: 10px;
      position: absolute;
    }
  }
`;

const MissionItemText = styled(Text)`
  margin-top: 10%;
`;

const SectionTitle = styled(Text)`
  margin: 0;
  font-size: 48px;

  @media screen and (max-width: 650px) {
    font-size: 22px;
    margin-bottom: 10%;
  }
`;

const SectionSubTitle = styled(Text)`
  margin: 0;
  color: rgba(255, 255, 255, 0.1);
  font-size: 56px;

  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const UserBio = styled(Row)`
  padding-left: 10%;
  padding-bottom: 10%;
  padding-right: 10%;
  gap: 10%;

  @media screen and (max-width: 650px) {
    flex-wrap: wrap;
  }
`;

const UserBioProfilePic = styled.img`
  border: 10px solid #000d30;
  width: 250px;
`;

interface AboutUsPageState {
  navTo: string;
}

export const AboutUsPage: React.FC<AboutUsProps> = ({ theme, toggleTheme }) => {
  const stanRef = useRef(null);
  const vicRef = useRef(null);
  const eliskaRef = useRef(null);
  const jazzyRef = useRef(null);
  const location = useLocation();
  const locationState = location.state as AboutUsPageState;

  useEffect(() => {
    Aos.init({ duration: 3000 });
  }, []);

  const onLinkedInClick = (link) => {
    window.location.assign(link);
  };

  const onMailClick = (link) => {
    window.location.assign(link);
  };

  useEffect(() => {
    if (locationState === null)  {
      return;
    }
    if (locationState.navTo === "stan") {
      stanRef.current.scrollIntoView();
    } else if (locationState.navTo === "vic") {
      vicRef.current.scrollIntoView();
    } else if (locationState.navTo === "eliska") {
      eliskaRef.current.scrollIntoView();
    } else {
      jazzyRef.current.scrollIntoView();
    }
  }, [locationState]);

  return (
    <div>
      <NavBar theme={theme} themeToggler={toggleTheme} />
      <AboutUsSection>
        <AboutUsInfo data-aos="fade-down">
          <SectionTitle bold={true} size={"48px"}>
            ABOUT US
          </SectionTitle>
          <SectionSubTitle size={"64px"} style={{ marginBottom: "5%" }}>
            WHO WE ARE
          </SectionSubTitle>
          <Text bold={true}>
            At ComposeNFT we are reimagining the way music is shared, created
            and consumed using Web3. Our goal is to empower the common musician
            and their fan bases around the globe.
          </Text>
        </AboutUsInfo>
      </AboutUsSection>
      <OurMissionSection>
        <OurMissionInfo data-aos="fade-down">
          <SectionTitle bold={true} size={"48px"}>
            OUR MISSION
          </SectionTitle>
          <SectionSubTitle size={"64px"}>WHAT DRIVES US</SectionSubTitle>

          <MissionItems>
            <MissionItem>
              <MissionItemText bold={true}>
                Introduce complete transparency and fairness in the music
                industry via the blockchain
              </MissionItemText>
            </MissionItem>

            <MissionItem>
              <MissionItemText bold={true}>
                Enable content creators to freely and fairly use the music of
                their choice in their work
              </MissionItemText>
            </MissionItem>

            <MissionItem>
              <MissionItemText bold={true}>
                Empower every musician to control the rights to their music.
              </MissionItemText>
            </MissionItem>

            <MissionItem>
              <MissionItemText bold={true}>
                Disrupt the music industries current revenue distribution model
                by allowing fans to invest in artists
              </MissionItemText>
            </MissionItem>
          </MissionItems>
        </OurMissionInfo>
      </OurMissionSection>
      <TeamSection>
        <div data-aos="fade-down">
          <Column>
            <SectionTitle bold={true}>MEET THE TEAM</SectionTitle>
            <SectionSubTitle size={"64px"} style={{ marginBottom: "5%", color: 'rgba(255, 255, 255, 0.2)'}}>
              THE CREATORS, IMAGINERS, PROBLEM SOLVERS
            </SectionSubTitle>
          </Column>

          <UserBio ref={stanRef}>
            <UserBioProfilePic src={stan} />
            <Column>
              <Text bold={true} size={"40px"} style={{ marginBottom: "0px" }}>
                Stanton Thomas
              </Text>
              <Text bold={true}>Founder</Text>
              <div>
                <ImageButton
                  bg={email_icon}
                  onClick={() => onMailClick("mailto:stan@composenft.io")}
                  style={{ marginRight: "10px", paddingLeft: "0px" }}
                />
                <ImageButton
                  bg={linkedin_icon}
                  onClick={() =>
                    onLinkedInClick(
                      "https://www.linkedin.com/in/stanton-thomas1/"
                    )
                  }
                  style={{
                    marginRight: "auto",
                    paddingLeft: "0px",
                    backgroundSize: "cover",
                  }}
                />
              </div>

              <Text>
                Stanton hails from big tech <b>Product Management </b>where he
                worked on enterprise cloud solutions for some of the world
                largest corporations. In addition, Stanton has a strong affinity
                for web3, he has been in involved in the space since 2019 and
                has even worked with Coinbase in the past. Stanton holds a BA
                from <b>UCLA </b>and is currently a{" "}
                <b>MBA Candidate at Cornell University</b>
              </Text>
            </Column>
          </UserBio>

          <UserBio ref={vicRef}>
            <UserBioProfilePic src={vic} />
            <Column>
              <Text bold={true} size={"40px"} style={{ marginBottom: "0px" }}>
                Victor Liang
              </Text>
              <Text bold={true}>Founder</Text>

              <div>
                <ImageButton
                  bg={email_icon}
                  onClick={() => onMailClick("mailto:vic@composenft.io")}
                  style={{ marginRight: "10px", paddingLeft: "0px" }}
                />
                <ImageButton
                  bg={linkedin_icon}
                  onClick={() =>
                    onLinkedInClick(
                      "https://www.linkedin.com/in/victor-liang-372213a4/"
                    )
                  }
                  style={{ marginRight: "auto", paddingLeft: "0px" }}
                />
              </div>
              <Text>
                An engineer who worked for <b>Snap</b>, <b>Amazon</b>, and{" "}
                <b>Wayfair</b>, Victor has architected and led numerous features
                scaled to billions of users.Victor holds a B.S in Computer
                Engineering from <b>Northeastern University</b>.
              </Text>
            </Column>
          </UserBio>
          <UserBio ref={eliskaRef}>
            <UserBioProfilePic src={eliska} />
            <Column>
              <Text bold={true} size={"40px"} style={{ marginBottom: "0px" }}>
                Eliska Patockova
              </Text>
              <Text bold={true}>Engineer</Text>
              <div>
                <ImageButton
                  bg={email_icon}
                  onClick={() =>
                    onMailClick("mailto:patockovaeliska@gmail.com")
                  }
                  style={{ marginRight: "10px", paddingLeft: "0px" }}
                />
                <ImageButton
                  bg={linkedin_icon}
                  onClick={() =>
                    onLinkedInClick(
                      "https://www.linkedin.com/in/eliska-patockova-114a7919a/"
                    )
                  }
                  style={{ marginRight: "auto", paddingLeft: "0px" }}
                />
              </div>
              <Text>
                Eliska has a background in <b>Software Engineering</b> and
                Quality Assurance in big tech. She earned her undergraduate
                degree at <b>UCLA</b> and is working toward a Master’s degree in
                Computer Science at <b>Cornell University</b>.
              </Text>
            </Column>
          </UserBio>
          <UserBio ref={jazzyRef}>
            <UserBioProfilePic src={jazzy} />
            <Column>
              <Text bold={true} size={"40px"} style={{ marginBottom: "0px" }}>
                Jazzy Liang
              </Text>
              <div>
                <ImageButton
                  bg={email_icon}
                  onClick={() => onMailClick("mailto:jazzy@composenft.io")}
                  style={{ marginRight: "10px", paddingLeft: "0px" }}
                />
                <ImageButton
                  bg={linkedin_icon}
                  onClick={() =>
                    onLinkedInClick("https://www.linkedin.com/in/jazzy-ml/")
                  }
                  style={{ marginRight: "auto", paddingLeft: "0px" }}
                />
              </div>
              <Text bold={true}>Product Designer</Text>
              <Text>
                Jazzy is a designer who graduated from <b>UC Davis</b>. Creating
                friendly and meaningful designs to tackle complex challenges has
                been the passion that drives her to where she is today.
              </Text>
            </Column>
          </UserBio>
        </div>
      </TeamSection>
      <Footer />
    </div>
  );
};
